<script setup lang="ts">
import { computed, type Component } from 'vue'
import { Button } from '@/components/ui/button'
import { PhSpinner } from '@phosphor-icons/vue'
import { cn } from '@/lib/utils'

const props = withDefaults(defineProps<{
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'accent' | 'pi' | 'danger' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
  icon?: Component
  iconRight?: Component
  href?: string
  target?: string
  rel?: string
  to?: string | object
  asChild?: boolean
  as?: any
}>(), { type: 'button', variant: 'primary', size: 'md' })

const emit = defineEmits<{ click: [event: MouseEvent] }>()

const variantClasses = computed(() => {
  switch (props.variant) {
    case 'primary':
      return 'bg-primary text-primary-foreground font-semibold hover:bg-primary-hover active:bg-primary-active shadow-xs button-fill'
    case 'secondary':
      return 'border border-line-2 bg-transparent text-ink-2 hover:bg-surface-2 active:bg-surface-3 font-semibold button-outline'
    case 'accent':
      return 'bg-cyan-500/15 text-accent-cyan hover:bg-cyan-500/25 active:bg-cyan-500/30 font-semibold button-tonal'
    case 'pi':
      return 'bg-purple-600 text-white hover:bg-purple-500 active:bg-purple-700 font-semibold shadow-xs button-fill'
    case 'danger':
      return 'bg-red-500/15 text-accent-red border border-red-500/30 hover:bg-red-500/25 active:bg-red-500/30 font-semibold button-tonal'
    case 'ghost':
      return 'bg-transparent text-ink-3 hover:bg-surface-2 active:bg-surface-3 hover:text-ink-2'
    default:
      return 'bg-primary text-primary-foreground font-semibold button-fill'
  }
})

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'h-8 px-3 text-xs button-small'
    case 'lg':
      return 'h-12 px-6 text-base font-semibold button-large'
    case 'md':
    default:
      return 'h-10 px-4 text-sm font-semibold'
  }
})

const computedAs = computed(() => {
  if (props.as) return props.as
  if (props.href) return 'a'
  return 'button'
})
</script>

<template>
  <Button
    :as="computedAs"
    :as-child="props.asChild"
    :type="computedAs === 'button' ? props.type : undefined"
    :href="props.href"
    :target="props.target"
    :rel="props.rel"
    :disabled="props.disabled || props.loading"
    :aria-busy="props.loading || undefined"
    :class="cn(
      'button rounded-[12px] transition-transform active:scale-[0.97]',
      props.fullWidth ? 'w-full' : 'inline-flex w-auto',
      variantClasses,
      sizeClasses,
    )"
    @click="!props.disabled && !props.loading && emit('click', $event)"
  >
    <span class="inline-flex items-center justify-center gap-2">
      <PhSpinner v-if="props.loading" class="w-4 h-4 animate-spin shrink-0" />
      <slot v-if="!props.loading" name="icon"><component v-if="props.icon" :is="props.icon" class="w-4 h-4 shrink-0" /></slot>
      <slot />
      <slot v-if="!props.loading" name="icon-right"><component v-if="props.iconRight" :is="props.iconRight" class="w-4 h-4 shrink-0" /></slot>
    </span>
  </Button>
</template>
